import React, { useState, useEffect, useCallback } from "react";
import {
  PageHeader, KPIGrid, DataTable, StandardModal, StatusBadge,
  FilterPills, Alert, Btn, FormField,
  inputStyle, textareaStyle,
  colors, spacing, typography, radius,
} from "../design-system";

const BASE = import.meta.env.VITE_API_URL || "http://localhost:8000/api";
const API = `${BASE}/accredited-courses`;

const CATEGORY_COLORS = {
  "Lead Auditor":     "#1e40af",
  "Internal Auditor": "#0891b2",
  "Foundation":       "#059669",
  "Awareness":        "#d97706",
  "ISO 9001":         "#7c3aed",
  "ISO 14001":        "#065f46",
  "ISO 45001":        "#b45309",
  "ISO 27001":        "#1d4ed8",
};

function CategoryTag({ category }) {
  const color = CATEGORY_COLORS[category] || colors.textMuted;
  return (
    <span style={{
      background: color + "22", color, border: `1px solid ${color}55`,
      padding: "2px 10px", borderRadius: radius.pill,
      fontSize: typography.fontSizes.xs, fontWeight: typography.fontWeights.semibold,
    }}>{category}</span>
  );
}

const ACCRED_MAP = {
  approved:  { bg: "#dcfce7", text: colors.success,  label: "Accredited" },
  pending:   { bg: "#fef9c3", text: colors.warning,  label: "Pending" },
  suspended: { bg: "#fee2e2", text: colors.danger,   label: "Suspended" },
  revoked:   { bg: "#f3f4f6", text: colors.textMuted, label: "Revoked" },
  expired:   { bg: "#fff7ed", text: "#c2410c",       label: "Expired" },
  none:      { bg: "#f3f4f6", text: colors.textMuted, label: "Not Accredited" },
};

function AccredBadge({ status }) {
  const c = ACCRED_MAP[status] || ACCRED_MAP.none;
  return (
    <span style={{
      background: c.bg, color: c.text,
      padding: "2px 10px", borderRadius: radius.pill,
      fontSize: typography.fontSizes.xs, fontWeight: typography.fontWeights.semibold,
    }}>{c.label}</span>
  );
}

const EMPTY_FORM = { title: "", description: "", duration_hours: "", category: "", provider_company_id: "" };

export default function ManageCourses() {
  const [courses, setCourses]           = useState([]);
  const [certBodies, setCertBodies]     = useState([]);
  const [loading, setLoading]           = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [search, setSearch]             = useState("");
  const [showCreate, setShowCreate]     = useState(false);
  const [editCourse, setEditCourse]     = useState(null);
  const [form, setForm]                 = useState(EMPTY_FORM);
  const [saving, setSaving]             = useState(false);
  const [msg, setMsg]                   = useState("");
  const [msgType, setMsgType]           = useState("success");

  const token    = localStorage.getItem("token");
  const userRole = JSON.parse(localStorage.getItem("user") || "{}").role;
  const isSuperAdmin = userRole === "super_admin";
  const headers  = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const fetchCourses = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== "all") params.set("status", filterStatus);
      if (filterCategory !== "all") params.set("category", filterCategory);
      const res = await fetch(`${API}/?${params}`, { headers });
      if (!res.ok) throw new Error("Failed to load courses");
      setCourses(await res.json());
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterCategory]);

  useEffect(() => { fetchCourses(); }, [fetchCourses]);

  useEffect(() => {
    fetch(`${BASE}/cert-bodies/`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(data => setCertBodies(Array.isArray(data) ? data : (data.cert_bodies || [])))
      .catch(() => {});
  }, []);

  const flash = (m, t = "success") => { setMsg(m); setMsgType(t); setTimeout(() => setMsg(""), 3500); };

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        title:               form.title,
        description:         form.description || null,
        duration_hours:      form.duration_hours ? parseInt(form.duration_hours) : null,
        category:            form.category || null,
        provider_company_id: form.provider_company_id ? parseInt(form.provider_company_id) : null,
      };
      const url    = editCourse ? `${API}/${editCourse.id}` : `${API}/`;
      const method = editCourse ? "PATCH" : "POST";
      const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Failed"); }
      flash(editCourse ? "Course updated." : "Course created.");
      setShowCreate(false);
      setEditCourse(null);
      setForm(EMPTY_FORM);
      fetchCourses();
    } catch (e) { flash("Error: " + e.message, "error"); }
    finally { setSaving(false); }
  }

  async function handleToggleStatus(course) {
    setSaving(true);
    try {
      const newStatus = course.status === "active" ? "suspended" : "active";
      const res = await fetch(`${API}/${course.id}`, {
        method: "PATCH", headers, body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Failed"); }
      flash(`Course ${newStatus}.`);
      fetchCourses();
    } catch (e) { flash("Error: " + e.message, "error"); }
    finally { setSaving(false); }
  }

  async function handleDelete(course) {
    if (!window.confirm(`Delete course "${course.title}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API}/${course.id}`, { method: "DELETE", headers });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Failed"); }
      flash("Course deleted.");
      fetchCourses();
    } catch (e) { flash("Error: " + e.message, "error"); }
  }

  const openEdit = (course) => {
    setEditCourse(course);
    setForm({
      title:               course.title || "",
      description:         course.description || "",
      duration_hours:      course.duration_hours || "",
      category:            course.category || "",
      provider_company_id: course.provider_company_id || "",
    });
    setShowCreate(true);
  };

  const closeModal = () => { setShowCreate(false); setEditCourse(null); setForm(EMPTY_FORM); };

  // Dynamic category filter options
  const categories = [...new Set(courses.map(c => c.category).filter(Boolean))];
  const categoryOptions = [
    { value: "all", label: "All Categories" },
    ...categories.map(c => ({ value: c, label: c })),
  ];

  const statusOptions = [
    { value: "all",       label: "All Status" },
    { value: "active",    label: "Active" },
    { value: "suspended", label: "Suspended" },
  ];

  const filtered = courses.filter(c =>
    search === "" || c.title.toLowerCase().includes(search.toLowerCase())
  );

  const accreditedCount = courses.filter(c => c.accreditation_status === "approved").length;

  const kpis = [
    { label: "Total Courses", value: courses.length,                                      icon: "📚", color: colors.primary },
    { label: "Active",        value: courses.filter(c => c.status === "active").length,   icon: "✅", color: colors.success },
    { label: "Accredited",    value: accreditedCount,                                     icon: "🎖️", color: colors.purple },
    { label: "Suspended",     value: courses.filter(c => c.status === "suspended").length, icon: "⏸️", color: colors.danger },
  ];

  const columns = [
    {
      key: "title", label: "Course",
      render: (v, row) => (
        <div>
          <div style={{ fontWeight: typography.fontWeights.semibold, color: colors.textPrimary }}>{v}</div>
          {row.description && (
            <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, marginTop: 2 }}>
              {row.description.slice(0, 60)}{row.description.length > 60 ? "…" : ""}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "category", label: "Category",
      render: v => v ? <CategoryTag category={v} /> : <span style={{ color: colors.textMuted }}>—</span>,
    },
    {
      key: "duration_hours", label: "Duration",
      render: v => v ? `${v}h` : "—",
    },
    {
      key: "provider_name", label: "Provider",
      render: v => v || "—",
    },
    {
      key: "status", label: "Status",
      render: v => <StatusBadge status={v} />,
    },
    {
      key: "accreditation_status", label: "Accreditation",
      render: v => <AccredBadge status={v} />,
    },
    {
      key: "_actions", label: "", align: "right",
      render: (_, course) => {
        if (!isSuperAdmin) return null;
        return (
          <div style={{ display: "flex", gap: spacing.xs, justifyContent: "flex-end" }}>
            <Btn size="sm" variant="ghost" onClick={() => openEdit(course)}>Edit</Btn>
            <Btn
              size="sm"
              variant={course.status === "active" ? "danger" : "success"}
              onClick={() => handleToggleStatus(course)}
            >
              {course.status === "active" ? "Suspend" : "Activate"}
            </Btn>
            <Btn size="sm" variant="danger" onClick={() => handleDelete(course)}>Delete</Btn>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <PageHeader
        title="Accredited Courses"
        subtitle="Manage the governance-level course catalogue for ICSC course accreditation."
        actions={isSuperAdmin && (
          <Btn onClick={() => { setEditCourse(null); setForm(EMPTY_FORM); setShowCreate(true); }}>+ Add Course</Btn>
        )}
      />

      <KPIGrid kpis={kpis} columns={4} />

      {msg && <Alert type={msgType} message={msg} onClose={() => setMsg("")} />}

      {/* Filters + Search */}
      <div style={{ display: "flex", gap: spacing.md, marginBottom: spacing.lg, flexWrap: "wrap", alignItems: "center" }}>
        <FilterPills options={statusOptions} value={filterStatus} onChange={setFilterStatus} />
        <FilterPills options={categoryOptions} value={filterCategory} onChange={setFilterCategory} color={colors.purple} />
        <input
          type="text"
          placeholder="Search courses..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ ...inputStyle, width: 220, marginLeft: "auto" }}
        />
      </div>

      <DataTable
        columns={columns}
        rows={filtered}
        loading={loading}
        emptyMessage="No courses found."
        keyField="id"
      />

      {/* Create / Edit Modal */}
      {showCreate && (
        <StandardModal
          title={editCourse ? "Edit Course" : "Add Course"}
          onClose={closeModal}
          footer={
            <>
              <Btn variant="secondary" onClick={closeModal}>Cancel</Btn>
              <Btn type="submit" form="manage-course-form" loading={saving}>
                {editCourse ? "Update Course" : "Create Course"}
              </Btn>
            </>
          }
        >
          <form id="manage-course-form" onSubmit={handleSave}>
            <div style={{ display: "flex", flexDirection: "column", gap: spacing.md }}>
              <FormField label="Course Title" required>
                <input
                  required
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  style={inputStyle}
                />
              </FormField>

              <FormField label="Description">
                <textarea
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  rows={3}
                  style={textareaStyle}
                />
              </FormField>

              <div style={{ display: "flex", gap: spacing.md }}>
                <FormField label="Category" style={{ flex: 1 }}>
                  <input
                    value={form.category}
                    onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                    placeholder="e.g. Lead Auditor"
                    style={inputStyle}
                  />
                </FormField>
                <FormField label="Duration (hours)" style={{ flex: 1 }}>
                  <input
                    type="number"
                    min="1"
                    value={form.duration_hours}
                    onChange={e => setForm(f => ({ ...f, duration_hours: e.target.value }))}
                    style={inputStyle}
                  />
                </FormField>
              </div>

              <FormField label="Provider / Certification Body">
                <select
                  value={form.provider_company_id}
                  onChange={e => setForm(f => ({ ...f, provider_company_id: e.target.value }))}
                  style={{ ...inputStyle }}
                >
                  <option value="">— ICSC-owned (no provider) —</option>
                  {certBodies.map(cb => (
                    <option key={cb.id} value={cb.id}>{cb.name}</option>
                  ))}
                </select>
              </FormField>
            </div>
          </form>
        </StandardModal>
      )}
    </div>
  );
}
